import React from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Eye,
  TrendingUp,
  Heart,
  MousePointer,
  Download,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import DashboardLayout from '../components/DashboardLayout';
import OverviewTab from '../components/analytics/OverviewTab';
import PostsTab from '../components/analytics/PostsTab';
import EngagementTab from '../components/analytics/EngagementTab';
import AudienceTab from '../components/analytics/AudienceTab';
import TimingTab from '../components/analytics/TimingTab';
import useAnalyticsData from '../hooks/useAnalyticsData';
import { formatNumber } from '../components/analytics/constants';

const AnalyticsPage = () => {
  const {
    analytics,
    topPosts,
    bestTimes,
    audience,
    calendars,
    platformHealth,
    platformData,
    pieData,
    postsPerformance,
    postDetail,
    selectedPost,
    loading,
    refreshing,
    postsLoading,
    postDetailLoading,
    selectedCalendar,
    setSelectedCalendar,
    dateRange,
    setDateRange,
    activeTab,
    setActiveTab,
    postsSortBy,
    setPostsSortBy,
    postsSortOrder,
    toggleSortOrder,
    fetchPostDetail,
    handleExportAnalytics,
    handleRefreshAnalytics,
    getDataSourceLabel,
  } = useAnalyticsData();

  const stats = analytics
    ? [
        { title: 'Total Reach', value: formatNumber(analytics.summary?.total_reach), change: '+12.5%', icon: Eye, color: 'bg-blue-500', description: 'People who saw your content' },
        { title: 'Impressions', value: formatNumber(analytics.summary?.total_impressions), change: '+8.3%', icon: TrendingUp, color: 'bg-purple-500', description: 'Total content views' },
        { title: 'Engagement', value: formatNumber(analytics.summary?.total_engagement), change: '+23.1%', icon: Heart, color: 'bg-pink-500', description: 'Likes, comments & shares' },
        { title: 'Clicks', value: formatNumber(analytics.summary?.total_clicks), change: '+15.7%', icon: MousePointer, color: 'bg-emerald-500', description: 'Link clicks' },
      ]
    : [];

  const dataSourceLabel = getDataSourceLabel();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-emerald-500" />
              Analytics Dashboard
            </h1>
            <p className="text-slate-600 mt-1">Track and optimize your social media performance</p>
            <p className={`text-xs mt-0.5 ${dataSourceLabel.color}`}>{dataSourceLabel.label}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedCalendar} onValueChange={setSelectedCalendar}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Calendars" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Calendars</SelectItem>
                {calendars.map((cal) => (
                  <SelectItem key={cal.calendar_id} value={cal.calendar_id}>
                    {cal.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" data-testid="export-btn" onClick={handleExportAnalytics}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button
              variant="outline"
              onClick={handleRefreshAnalytics}
              disabled={refreshing}
              data-testid="refresh-analytics-btn"
            >
              {refreshing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Refreshing...</>
              ) : (
                <><RefreshCw className="h-4 w-4 mr-2" />Refresh</>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className="border-slate-100 hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="h-5 w-5 text-white" />
                    </div>
                    <Badge variant="secondary" className="bg-emerald-50 text-emerald-600">
                      {stat.change}
                    </Badge>
                  </div>
                  <div className="mt-3">
                    <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                    <p className="text-sm font-medium text-slate-700">{stat.title}</p>
                    <p className="text-xs text-slate-500 mt-1">{stat.description}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Tab Navigation + Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts">Posts</TabsTrigger>
            <TabsTrigger value="engagement" data-testid="tab-engagement">Engagement</TabsTrigger>
            <TabsTrigger value="audience" data-testid="tab-audience">Audience</TabsTrigger>
            <TabsTrigger value="timing" data-testid="tab-timing">Best Times</TabsTrigger>
          </TabsList>

          <OverviewTab
            analytics={analytics}
            topPosts={topPosts}
            platformHealth={platformHealth}
            pieData={pieData}
          />

          <PostsTab
            postsPerformance={postsPerformance}
            postsLoading={postsLoading}
            postsSortBy={postsSortBy}
            setPostsSortBy={setPostsSortBy}
            postsSortOrder={postsSortOrder}
            toggleSortOrder={toggleSortOrder}
            selectedPost={selectedPost}
            fetchPostDetail={fetchPostDetail}
            postDetail={postDetail}
            postDetailLoading={postDetailLoading}
          />

          <EngagementTab analytics={analytics} platformData={platformData} />

          <AudienceTab audience={audience} analytics={analytics} />

          <TimingTab bestTimes={bestTimes} />
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
